import React, { useEffect, useState } from 'react';
import { HolderOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useAuthStore from '../../store/authStore';

// Kaydedilmiş sıradaki id'lerden hâlâ var olanları koru; kayıtta olmayan
// (yeni eklenmiş ya da koşula bağlı yeni görünür olmuş) widget'ları
// VARSAYILAN SIRADAKİ KOMŞUSUNUN yanına yerleştir.
//
// ⚠ Eskiden sona ekleniyordu (`[...known, ...missing]`) ve bu, uyarı
// kartlarını sessizce işe yaramaz hale getiriyordu: panoyu bir kez bile
// sürükleyip sıralamış bir kullanıcının kaydında o an görünmeyen kart
// bulunmuyor, kart sonradan belirdiğinde 19 widget'ın EN ALTINA düşüyordu.
// "Öğretmensiz Dersler" uyarısı tam olarak böyle kayboldu — kart vardı,
// veri vardı, kimse görmedi.
//
// Kullanıcının kendi sıralaması BOZULMUYOR: yalnızca kayıtta hiç olmayan
// id'ler yerleştiriliyor, bilinenlerin göreli sırası aynen korunuyor.
const reconcileOrder = (savedOrder, currentIds) => {
  const known = savedOrder.filter(id => currentIds.includes(id));
  if (known.length === currentIds.length) return known;

  const sonuc = [...known];
  currentIds.forEach((id, i) => {
    if (sonuc.includes(id)) return;
    // Varsayılan sırada kendinden ÖNCE gelen ve sonuçta zaten bulunan en
    // yakın id'yi bul; hemen onun arkasına gir. Hiçbiri yoksa (listenin
    // başındaki bir kart) en başa.
    let hedef = 0;
    for (let j = i - 1; j >= 0; j--) {
      const yer = sonuc.indexOf(currentIds[j]);
      if (yer !== -1) { hedef = yer + 1; break; }
    }
    sonuc.splice(hedef, 0, id);
  });
  return sonuc;
};

const SortableWidget = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        {...attributes}
        {...listeners}
        title="Sürükleyerek sırala"
        style={{
          position: 'absolute', top: 4, right: 4, zIndex: 5,
          width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'grab', color: '#c0c0c0', borderRadius: 6,
        }}
        onMouseDown={e => e.currentTarget.style.color = '#1677ff'}
        onMouseUp={e => e.currentTarget.style.color = '#c0c0c0'}
      >
        <HolderOutlined style={{ fontSize: 16 }} />
      </div>
      {children}
    </div>
  );
};

/**
 * widgets: [{ id: string, content: ReactNode }] — sadece o an görünür (koşulu
 * sağlanan) widget'lar geçilmeli. Sıra kullanıcı+dashboard bazlı localStorage'da
 * tutulur, çoklu cihaz senkronizasyonu yok (bkz. usePersistedPageSize deseni).
 */
const DraggableDashboard = ({ storageKey, widgets }) => {
  const { user } = useAuthStore();
  const ids = widgets.map(w => w.id);
  const fullKey = `dashboardOrder:${user?.id}:${storageKey}`;

  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(fullKey) || 'null');
      if (Array.isArray(saved)) return reconcileOrder(saved, ids);
    } catch { /* bozuk kayıt — varsayılan sıraya düş */ }
    return ids;
  });

  // widget seti değişti (veri geldi/gitti, koşullu bölüm görünür oldu) —
  // kayıtlı sırayı bozmadan yeni id'leri sona ekle.
  useEffect(() => {
    setOrder(prev => {
      const next = reconcileOrder(prev, ids);
      const same = next.length === prev.length && next.every((v, i) => v === prev[i]);
      return same ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join('|')]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setOrder(prev => {
      const next = arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id));
      try { localStorage.setItem(fullKey, JSON.stringify(next)); } catch { /* localStorage dolu/kapalı olabilir */ }
      return next;
    });
  };

  const contentById = Object.fromEntries(widgets.map(w => [w.id, w.content]));

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <div className="space-y-5">
          {order.map(id => contentById[id] != null && (
            <SortableWidget key={id} id={id}>{contentById[id]}</SortableWidget>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableDashboard;
